import { useEffect, useState } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import Button from 'domains/core/components/Button';
import TextField from 'domains/core/components/TextField';
import { AIChatFeature, GUARDRAIL_TYPES } from 'models/enums';

import type { GuardrailBody } from 'hooks/mutations/useGuardrailMutations';
import type { AIGuardrailRow } from 'hooks/queries/useGetGuardrailsList';
import type { GuardrailType } from 'models/enums';

const isSystemPromptType = (type: GuardrailType) => type.includes('system-prompt');

type Props = {
    open: boolean;
    onClose: () => void;
    onSubmit: (body: GuardrailBody) => void;
    isSubmitting: boolean;
    editingGuardrail?: AIGuardrailRow | null;
};

const emptyForm: GuardrailBody = {
    guardrail_name: '',
    feature: AIChatFeature.COPY_GENERATOR,
    guardrail_type: 'input',
    system_prompt: '',
    error_message: '',
    sort_order: null,
    is_enabled: true,
};

type FieldLabelProps = {
    label: string;
    required?: boolean;
    htmlFor?: string;
};

const FieldLabel = ({ label, required, htmlFor }: FieldLabelProps) => (
    <Stack
        component={htmlFor ? 'label' : 'div'}
        direction="row"
        alignItems="center"
        spacing={0.75}
        htmlFor={htmlFor}
        sx={{ cursor: htmlFor ? 'pointer' : undefined, width: 'fit-content' }}
    >
        <Typography component="span" color="text.primary" fontSize={16} fontWeight={500}>
            {label}
        </Typography>
        {required && (
            <Box
                aria-hidden
                component="span"
                sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }}
            />
        )}
    </Stack>
);

const GuardrailFormDialog = ({ open, onClose, onSubmit, isSubmitting, editingGuardrail }: Props) => {
    const [form, setForm] = useState<GuardrailBody>(emptyForm);

    useEffect(() => {
        if (editingGuardrail) {
            setForm({
                guardrail_name: editingGuardrail.guardrail_name,
                feature: editingGuardrail.feature,
                guardrail_type: editingGuardrail.guardrail_type,
                system_prompt: editingGuardrail.system_prompt,
                error_message: editingGuardrail.error_message,
                sort_order: editingGuardrail.sort_order,
                is_enabled: editingGuardrail.is_enabled,
            });
        } else {
            setForm(emptyForm);
        }
    }, [editingGuardrail, open]);

    const handleChange = (field: keyof GuardrailBody, value: unknown) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        onSubmit(form);
    };

    const systemPromptType = isSystemPromptType(form.guardrail_type);

    const isValid =
        form.guardrail_name.trim() !== '' &&
        form.feature.trim() !== '' &&
        form.system_prompt.trim() !== '' &&
        (systemPromptType || (form.error_message ?? '').trim() !== '');

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="guardrail-form-title">
            <DialogTitle id="guardrail-form-title">{editingGuardrail ? 'Edit Guardrail' : 'Add Guardrail'}</DialogTitle>

            <DialogContent sx={{ pt: '20px !important' }}>
                <Stack spacing={3}>
                    <Stack spacing={1}>
                        <FieldLabel label="Name" required htmlFor="guardrail-name" />
                        <TextField
                            id="guardrail-name"
                            value={form.guardrail_name}
                            onChange={(e) => handleChange('guardrail_name', e.target.value)}
                            fullWidth
                            data-testid="guardrail-name-input"
                            aria-label="Guardrail name"
                            placeholder="e.g. PHI Detection"
                        />
                    </Stack>

                    <Stack spacing={1}>
                        <FieldLabel label="Feature" required htmlFor="guardrail-feature" />
                        <Autocomplete
                            options={Object.values(AIChatFeature)}
                            value={form.feature}
                            onChange={(_e, newValue) =>
                                handleChange('feature', newValue ?? AIChatFeature.COPY_GENERATOR)
                            }
                            disableClearable
                            data-testid="guardrail-feature-input"
                            renderInput={(params) => (
                                <TextField {...params} id="guardrail-feature" aria-label="Feature name" />
                            )}
                            sx={{ width: '100%' }}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <Stack spacing={1} flex={1}>
                            <FieldLabel label="Type" required htmlFor="guardrail-type" />
                            <Autocomplete
                                options={GUARDRAIL_TYPES}
                                value={form.guardrail_type}
                                onChange={(_e, newValue) => handleChange('guardrail_type', newValue ?? 'input')}
                                disableClearable
                                data-testid="guardrail-type-select"
                                renderInput={(params) => (
                                    <TextField {...params} id="guardrail-type" aria-label="Guardrail type" />
                                )}
                                sx={{ width: '100%' }}
                            />
                        </Stack>

                        <Stack spacing={1} flex={1}>
                            <FieldLabel label="Sort Order" htmlFor="guardrail-sort-order" />
                            <TextField
                                id="guardrail-sort-order"
                                value={form.sort_order ?? ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    handleChange('sort_order', val === '' ? null : parseInt(val, 10));
                                }}
                                type="number"
                                fullWidth
                                data-testid="guardrail-sort-order-input"
                                aria-label="Execution sort order"
                                helperText="Lower numbers run first."
                            />
                        </Stack>
                    </Stack>

                    <Stack spacing={1}>
                        <FieldLabel label="System Prompt" required htmlFor="guardrail-system-prompt" />
                        <TextField
                            id="guardrail-system-prompt"
                            value={form.system_prompt}
                            onChange={(e) => handleChange('system_prompt', e.target.value)}
                            fullWidth
                            multiline
                            rows={5}
                            data-testid="guardrail-system-prompt-input"
                            aria-label="System prompt"
                            placeholder="Describe the guardrail's evaluation instructions for the AI..."
                        />
                    </Stack>

                    {!systemPromptType && (
                        <Stack spacing={1}>
                            <FieldLabel label="Error Message" required htmlFor="guardrail-error-message" />
                            <TextField
                                id="guardrail-error-message"
                                value={form.error_message ?? ''}
                                onChange={(e) => handleChange('error_message', e.target.value)}
                                fullWidth
                                multiline
                                rows={2}
                                data-testid="guardrail-error-message-input"
                                aria-label="Error message shown to user on violation"
                                placeholder="Message shown to the user when this guardrail is triggered..."
                            />
                        </Stack>
                    )}

                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                            px: 2,
                            py: 1.5,
                            borderRadius: '6px',
                            bgcolor: 'action.hover',
                        }}
                    >
                        <Stack spacing={0.25}>
                            <Typography color="text.primary" fontSize={16} fontWeight={500}>
                                Enabled
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Activate this guardrail for the selected feature
                            </Typography>
                        </Stack>
                        <Switch
                            checked={form.is_enabled}
                            onChange={(e) => handleChange('is_enabled', e.target.checked)}
                            data-testid="guardrail-enabled-switch"
                            aria-label="Enabled"
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: 'action.active' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: 'action.active' },
                            }}
                        />
                    </Stack>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} data-testid="guardrail-form-cancel" aria-label="Cancel">
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!isValid || isSubmitting}
                    data-testid="guardrail-form-submit"
                    aria-label={editingGuardrail ? 'Save changes' : 'Create guardrail'}
                >
                    {isSubmitting ? 'Saving...' : editingGuardrail ? 'Save' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GuardrailFormDialog;
